import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Check, Globe, Search } from 'lucide-react'
import { api } from '@/lib/api'
import type { SeoConfig } from '@shared/seo.config'
import { Seo } from '@/components/Seo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/sonner'

export default function Settings() {
  return (
    <>
      <Seo title="Cài đặt" noIndex />
      <div data-upbo-src="src/pages/Settings.tsx:22" className="mx-auto w-full max-w-3xl px-6 py-14">
        <h1 data-upbo-src="src/pages/Settings.tsx:23" className="text-3xl font-semibold tracking-tight">Cài đặt</h1>
        <p data-upbo-src="src/pages/Settings.tsx:24" className="mt-2 text-muted-foreground">
          Thay đổi ở đây có hiệu lực ngay, không cần deploy lại.
        </p>

        <Tabs defaultValue="seo" className="mt-8">
          <TabsList>
            <TabsTrigger value="seo"><Search className="size-3.5" />SEO &amp; AI Search</TabsTrigger>
            <TabsTrigger value="ai"><Bot className="size-3.5" />Crawler AI</TabsTrigger>
          </TabsList>
          <SeoSettings />
        </Tabs>
      </div>
    </>
  )
}

function SeoSettings() {
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({ queryKey: ['seo'], queryFn: api.seo.get })
  const [form, setForm] = useState<SeoConfig | null>(null)

  // Chỉ nạp form MỘT lần từ server. Nếu đồng bộ lại mỗi khi query đổi thì mọi
  // ký tự người dùng đang gõ dở sẽ bị ghi đè khi có refetch nền.
  useEffect(() => { if (data && !form) setForm(data) }, [data, form])

  const save = useMutation({
    mutationFn: (patch: Partial<SeoConfig>) => api.seo.save(patch),
    onSuccess: (saved) => {
      setForm(saved)
      qc.setQueryData(['seo'], saved)
      toast.success('Đã lưu. robots.txt, sitemap.xml và llms.txt cập nhật ngay.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (error) {
    return (
      <TabsContent value="seo" className="mt-6">
        <Card>
          <CardContent className="py-2 text-sm text-muted-foreground">
            Chưa đọc được cài đặt — thường là do <code data-upbo-src="src/pages/Settings.tsx:64" className="font-mono">DATABASE_URL</code> chưa
            được đặt. Website vẫn chạy với giá trị mặc định trong{' '}
            <code data-upbo-src="src/pages/Settings.tsx:66" className="font-mono">shared/seo.config.ts</code>.
          </CardContent>
        </Card>
      </TabsContent>
    )
  }

  if (isLoading || !form) {
    return (
      <TabsContent value="seo" className="mt-6 space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </TabsContent>
    )
  }

  const set = <K extends keyof SeoConfig>(key: K, value: SeoConfig[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f))

  const list = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean)

  return (
    <>
      <TabsContent value="seo" className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Globe className="size-4" />Nhận diện website</CardTitle>
            <CardDescription>
              Đây chính là những câu mà Google và các trợ lý AI trích ra khi giới thiệu website của bạn.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Field label="Tên website" hint="Vào tiêu đề trình duyệt, thẻ chia sẻ và dữ liệu có cấu trúc.">
              <Input value={form.siteName} onChange={(e) => set('siteName', e.target.value)} />
            </Field>
            <Field label="Mô tả" hint="1–2 câu. Viết cho người đọc, không nhồi từ khoá.">
              <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
            </Field>
            <Field label="Tên miền chính" hint="Ví dụ https://tencongty.com — để trống thì tự lấy theo tên miền đang truy cập.">
              <Input placeholder="https://" value={form.siteUrl} onChange={(e) => set('siteUrl', e.target.value)} />
            </Field>
            <Field label="Chủ đề chính" hint="Cách nhau bởi dấu phẩy. Đưa vào llms.txt để AI biết website nói về gì.">
              <Input
                value={form.keywords.join(', ')}
                onChange={(e) => set('keywords', list(e.target.value))}
              />
            </Field>
            <Field label="Liên kết mạng xã hội" hint="Cách nhau bởi dấu phẩy. Giúp AI nối website với đúng thương hiệu.">
              <Input
                placeholder="https://facebook.com/…, https://linkedin.com/…"
                value={form.sameAs.join(', ')}
                onChange={(e) => set('sameAs', list(e.target.value))}
              />
            </Field>
          </CardContent>
        </Card>
        <SaveBar pending={save.isPending} onSave={() => save.mutate(form)} />
      </TabsContent>

      <TabsContent value="ai" className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Bot className="size-4" />Cho AI đọc website</CardTitle>
            <CardDescription>
              Kênh marketing mới: khi người ta hỏi ChatGPT, Claude hay Perplexity về lĩnh vực của bạn,
              website chỉ được trích dẫn nếu các bot đó đọc được nó.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <Toggle
              checked={form.allowAiCrawlers}
              onChange={(v) => set('allowAiCrawlers', v)}
              title="Cho phép crawler của AI"
              body="Mở cửa cho GPTBot, ClaudeBot, PerplexityBot, Google-Extended và hơn 15 bot khác trong robots.txt. Tắt đi là chặn hẳn."
            />
            <Separator className="my-4" />
            <Toggle
              checked={form.prerenderForBots}
              onChange={(v) => set('prerenderForBots', v)}
              title="Trả nội dung sẵn cho bot"
              body="TỐI QUAN TRỌNG với ứng dụng dạng SPA. Hầu hết crawler của AI không chạy JavaScript, nên nếu tắt cái này họ chỉ thấy một trang trắng. Bật thì máy chủ chèn thẳng nội dung thật vào HTML gửi cho bot — người dùng thật không bị ảnh hưởng gì."
            />
            <Separator className="my-4" />
            <div data-upbo-src="src/pages/Settings.tsx:149" className="space-y-2 pt-1">
              <p data-upbo-src="src/pages/Settings.tsx:150" className="text-sm font-medium">File AI đang đọc được</p>
              <div data-upbo-src="src/pages/Settings.tsx:151" className="flex flex-wrap gap-2">
                {['/robots.txt', '/sitemap.xml', '/llms.txt', '/llms-full.txt'].map((f) => (
                  <a data-upbo-src="src/pages/Settings.tsx:153"
                    key={f}
                    href={f}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-md border px-2.5 py-1 font-mono text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {f}
                  </a>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        <SaveBar pending={save.isPending} onSave={() => save.mutate(form)} />
      </TabsContent>
    </>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div data-upbo-src="src/pages/Settings.tsx:175" className="space-y-2">
      <Label>{label}</Label>
      {children}
      {hint && <p data-upbo-src="src/pages/Settings.tsx:178" className="text-xs leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  )
}

function Toggle({ checked, onChange, title, body }: { checked: boolean; onChange: (v: boolean) => void; title: string; body: string }) {
  return (
    <div data-upbo-src="src/pages/Settings.tsx:185" className="flex items-start justify-between gap-6">
      <div data-upbo-src="src/pages/Settings.tsx:186" className="space-y-1">
        <p data-upbo-src="src/pages/Settings.tsx:187" className="text-sm font-medium">{title}</p>
        <p data-upbo-src="src/pages/Settings.tsx:188" className="text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} className="mt-1 shrink-0" />
    </div>
  )
}

function SaveBar({ pending, onSave }: { pending: boolean; onSave: () => void }) {
  return (
    <div data-upbo-src="src/pages/Settings.tsx:197" className="flex justify-end">
      <Button onClick={onSave} disabled={pending}>
        {pending ? 'Đang lưu…' : <><Check className="size-4" />Lưu thay đổi</>}
      </Button>
    </div>
  )
}
